import React, { useState, useEffect, useCallback } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import {
  SignalIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Modal from '../components/Modal';
import { getChannels, updateChannel, getChannelCustomers, getChannelProjects } from '../utils/api';

const ChannelManagement = () => {
  usePageTitle('Channel Management');

  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [saving, setSaving] = useState(false);

  const loadChannels = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getChannels({
        search,
        page: pagination.page,
        limit: 50,
        customer_filter: customerFilter
      });
      setChannels(response.data.channels);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error loading channels:', error);
      toast.error('Error loading channels');
    } finally {
      setLoading(false);
    }
  }, [search, pagination.page, customerFilter]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [customersRes, projectsRes] = await Promise.all([
          getChannelCustomers(),
          getChannelProjects()
        ]);
        setCustomers(customersRes.data);
        setProjects(projectsRes.data);
      } catch (error) {
        console.error('Error loading lookups:', error);
      }
    };
    loadLookups();
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (e) => {
    setCustomerFilter(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = customerFilter !== '';

  const openEditModal = (channel) => {
    setEditingChannel(channel);
    setSelectedCustomerId(channel.customer_id ? String(channel.customer_id) : '');
    setSelectedProjectId(channel.project_id ? String(channel.project_id) : '');
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingChannel(null);
    setSelectedCustomerId('');
    setSelectedProjectId('');
  };

  const handleSave = async () => {
    if (!editingChannel) return;

    setSaving(true);
    try {
      // Convert to number or null
      const customerId = selectedCustomerId === '' ? null : parseInt(selectedCustomerId, 10);
      const projectId = selectedProjectId === '' ? null : parseInt(selectedProjectId, 10);

      await updateChannel(editingChannel.id, { customer_id: customerId, project_id: projectId });
      toast.success('Channel updated successfully');
      closeEditModal();
      loadChannels();
    } catch (error) {
      console.error('Error updating channel:', error);
      toast.error(error.response?.data?.error || 'Error updating channel');
    } finally {
      setSaving(false);
    }
  };

  const getCustomerDisplay = (channel) => {
    if (channel.customer_name) {
      return channel.customer_name;
    }
    return '-';
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Channel Management</h1>
          <p className="page-subtitle">{pagination.total} channels total</p>
        </div>
      </div>

      <div className="card">
        <div className="search-bar" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-input-wrapper" style={{ flex: 1, minWidth: '200px' }}>
            <MagnifyingGlassIcon className="search-icon" style={{ width: 18, height: 18 }} />
            <input
              type="text"
              className="form-input search-input"
              placeholder="Search by channel or client name..."
              value={search}
              onChange={handleSearch}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FunnelIcon style={{ width: 18, height: 18, color: 'var(--color-text-tertiary)' }} />
            <select
              className="form-select"
              value={customerFilter}
              onChange={handleFilterChange}
              style={{ minWidth: '180px' }}
            >
              <option value="">All Clients</option>
              <option value="unassigned">Unassigned</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
            {hasActiveFilters && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setCustomerFilter('');
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                style={{ color: 'var(--color-accent-warning)' }}
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading-overlay">
            <div className="spinner" />
            <span>Loading channels...</span>
          </div>
        ) : channels.length > 0 ? (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Channel Name</th>
                    <th>Price Type</th>
                    <th>Client</th>
                    <th>Product</th>
                    <th style={{ width: 80 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((channel) => (
                    <tr key={channel.id}>
                      <td><strong>{channel.channel_name}</strong></td>
                      <td>{channel.price_type || '-'}</td>
                      <td>
                        {channel.customer_id ? (
                          <span style={{ color: 'var(--color-accent-success)' }}>
                            {getCustomerDisplay(channel)}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-text-tertiary)' }}>-</span>
                        )}
                      </td>
                      <td>
                        {channel.project_id ? (
                          <span style={{ color: 'var(--color-accent-primary)' }}>
                            {channel.project_name}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-text-tertiary)' }}>-</span>
                        )}
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => openEditModal(channel)}
                            title="Edit"
                          >
                            <PencilIcon style={{ width: 16, height: 16 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} items)
                </span>
                <button
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  ‹
                </button>
                {(() => {
                  const pages = [];
                  const start = Math.max(1, pagination.page - 2);
                  const end = Math.min(pagination.totalPages, start + 4);
                  for (let i = start; i <= end; i++) pages.push(i);
                  return pages.map((pageNum) => (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                      onClick={() => setPagination((prev) => ({ ...prev, page: pageNum }))}
                    >
                      {pageNum}
                    </button>
                  ));
                })()}
                <button
                  className="pagination-btn"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  ›
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <SignalIcon className="empty-state-icon" />
            <p className="empty-state-title">No channels found</p>
            <p>
              {search
                ? 'Try adjusting your search'
                : 'Upload a channel report to populate channels automatically.'}
            </p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={closeEditModal}
        title="Edit Channel"
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeEditModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        }
      >
        {editingChannel && (
          <div>
            <div className="form-group">
              <label className="form-label">Channel Name</label>
              <input
                type="text"
                className="form-input"
                value={editingChannel.channel_name}
                disabled
                style={{ background: 'var(--color-bg-tertiary)' }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Price Type</label>
              <input
                type="text"
                className="form-input"
                value={editingChannel.price_type || '-'}
                disabled
                style={{ background: 'var(--color-bg-tertiary)' }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Assign to Client</label>
              <select
                className="form-select"
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
              >
                <option value="">-- Not Assigned --</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name} {customer.code ? `(${customer.code})` : ''}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', marginTop: '0.5rem' }}>
                Select a customer to assign this channel to, or leave empty if not assigned.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Assign to Product</label>
              <select
                className="form-select"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
              >
                <option value="">-- Not Assigned --</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name} {project.code ? `(${project.code})` : ''}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', marginTop: '0.5rem' }}>
                Select a product (project) to assign this channel to.
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ChannelManagement;
